import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  Badge,
  Box,
  Field,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { LuFilter } from "react-icons/lu";
import { createColumnHelper } from "@tanstack/react-table";
import type { FunctionReturnType } from "convex/server";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { AuditAction, AuditEntityType, type AuditEntityTypeValue } from "@convex/schemaEnums";
import { dayParam, oneOf, searchUpdate, text } from "@/lib/search";
import { FilterCount } from "../components/FilterCount";
import { DataTable } from "../components/DataTable";
import { MultiSelectTags } from "../components/MultiSelectTags";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { TextLink } from "../components/TextLink";
import { actionLabel, formatAuditTime, moduleLabel, StepName } from "../components/auditDisplay";
import { Tooltip } from "../components/ui/tooltip";

// Derived, not described: see the note on ImpersonationTarget in Sidebar.tsx.
type AuditEntry = FunctionReturnType<typeof api.audit.list>["entries"][number];

// The act filter's choices, grouped by module, each group led by "every act
// in it" -- the value ending in a dot, which the query reads as a prefix.
const ACT_GROUPS = (() => {
  const groups = new Map<string, string[]>();
  for (const action of Object.values(AuditAction)) {
    const module = action.split(".")[0] ?? action;
    groups.set(module, [...(groups.get(module) ?? []), action]);
  }
  return [...groups.entries()].map(([module, actions]) => ({
    module,
    label: moduleLabel(module),
    actions,
  }));
})();

/** "2026-10-13" for a Date, in local time -- what an `<input type="date">` holds. */
function toDayString(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

type EntityFilter = NonNullable<AuditEntry["entityRef"]> & { label: string };

/**
 * The thing acted on, as a link to its page where it has one.
 *
 * A name inside a sentence, so a `TextLink`: visibly a link at rest, not only
 * on hover. Table cells that are nothing BUT a name underline on hover
 * instead; this is not one of those.
 */
function EntityName({ entity }: { entity: NonNullable<AuditEntry["entity"]> }) {
  const target = entity.link;
  if (target === null) {
    return <Text as="span" fontWeight="medium">{entity.label}</Text>;
  }
  return (
    <TextLink>
      {target.to === "personActivity"
        ? <Link to="/activities/$personActivityId" params={{ personActivityId: target.id }} search={{ from: "audit" }}>{entity.label}</Link>
        : target.to === "event"
          ? <Link to="/events/$eventId" params={{ eventId: target.id }}>{entity.label}</Link>
          : <Link to="/activities/admin/catalogue/$activityId" params={{ activityId: target.id }}>{entity.label}</Link>}
    </TextLink>
  );
}

const column = createColumnHelper<AuditEntry>();

// One line per entry. Everything that would wrap is either kept short or
// truncated with the full text on hover, so a week of the log scans as a list.
function buildColumns(onEntityFilter: (entity: EntityFilter) => void) {
  return [
    column.accessor("at", {
      header: "When",
      cell: ({ getValue }) => <Text whiteSpace="nowrap">{formatAuditTime(getValue())}</Text>,
    }),
    column.accessor("actorName", {
      header: "Who",
      cell: ({ row }) => (
        <HStack gap={2} whiteSpace="nowrap">
          <Text color={row.original.actorName === "CeMMbase" ? "fg.muted" : undefined}>
            {row.original.actorName}
          </Text>
          {/* The reason both are recorded: during impersonation the account at
              the keyboard and the identity it used are different people, and
              the log must never collapse them into one. */}
          {row.original.actedAsName === null ? null : (
            <Badge colorPalette="orange" variant="subtle">as {row.original.actedAsName}</Badge>
          )}
        </HStack>
      ),
    }),
    column.accessor("action", {
      header: "Act",
      cell: ({ getValue }) => {
        const { module, verb } = actionLabel(getValue());
        return (
          <Text whiteSpace="nowrap">
            <Text as="span" color="fg.muted">{module}</Text>
            {" › "}
            {verb}
          </Text>
        );
      },
    }),
    column.accessor("subjectName", {
      header: "About",
      cell: ({ getValue }) => (
        <Text whiteSpace="nowrap" color={getValue() === null ? "fg.muted" : undefined}>
          {getValue() ?? "—"}
        </Text>
      ),
    }),
    column.display({
      id: "what",
      header: "What",
      cell: ({ row }) => {
        const { entity, entityRef, part, detail, reason } = row.original;
        const full = [
          [[entity?.label, part].filter(Boolean).join(" › "), detail]
            .filter((piece) => piece !== "").join(": "),
          reason === "" ? null : `Reason: ${reason}`,
        ].filter((piece) => piece !== null).join(" · ");
        return (
          <HStack gap={1.5} minW={0}>
            {entity === null || entityRef === null ? null : (
              <Tooltip content={`Only entries about ${entity.label}`}>
                <IconButton
                  aria-label={`Only entries about ${entity.label}`}
                  size="2xs"
                  variant="ghost"
                  color="fg.muted"
                  onClick={() => onEntityFilter({ ...entityRef, label: entity.label })}
                >
                  <LuFilter />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip content={full}>
              <Text truncate maxW={{ base: "20rem", lg: "32rem", "2xl": "48rem" }}>
                {entity === null ? null : <EntityName entity={entity} />}
                {part === null ? null : (
                  <>
                    {entity === null ? null : " › "}
                    <StepName title={part} />
                  </>
                )}
                {entity === null && part === null ? null : ": "}
                {detail}
                {reason === "" ? null : (
                  <Text as="span" color="fg.muted">{` · Reason: ${reason}`}</Text>
                )}
              </Text>
            </Tooltip>
          </HStack>
        );
      },
    }),
  ];
}

/**
 * The institute-wide audit log (cicd-backlog §P14). Sys admin only; the query
 * refuses anybody else, and the router's error boundary says so.
 *
 * The date range is the filter that matters for cost: every read is bounded by
 * it, so it opens on the last week rather than on everything.
 */
function aWeekAgo(): string {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return toDayString(date);
}

export type AuditSearch = {
  /** A day, or "all" for the beginning. Absent is the last week. */
  from?: string;
  to?: string;
  person?: string;
  act?: string;
  /** "type:id" of one thing, and its name for the chip that says so. */
  about?: string;
  aboutName?: string;
};

export function validateAuditSearch(search: Record<string, unknown>): AuditSearch {
  const about = text(search.about);
  const [type, ...id] = (about ?? "").split(":");
  const validAbout = oneOf(type, Object.values(AuditEntityType)) !== undefined && id.join(":") !== "";
  return {
    from: search.from === "all" ? "all" : dayParam(search.from),
    to: dayParam(search.to),
    person: text(search.person),
    act: text(search.act),
    about: validAbout ? about : undefined,
    aboutName: validAbout ? text(search.aboutName) : undefined,
  };
}

export function AuditLogPage() {
  // All of it in the address: a filtered log is something to send to
  // somebody ("this is what happened to her deadline").
  const params = useSearch({ from: "/_authed/admin/audit-log" });
  const navigate = useNavigate({ from: "/admin/audit-log" });
  const setParams = (patch: AuditSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), ...searchUpdate });

  const fromDay = params.from === "all" ? "" : (params.from ?? aWeekAgo());
  // Empty means up to now.
  const toDay = params.to ?? "";
  const personIds = params.person === undefined ? [] : [params.person];
  const action = params.act ?? "";
  const entity: EntityFilter | null = params.about === undefined
    ? null
    : {
      type: params.about.split(":")[0] as AuditEntityTypeValue,
      id: params.about.split(":").slice(1).join(":"),
      label: params.aboutName ?? "this",
    };

  // One thing's history is bounded by the thing, not the week, and is usually
  // longer than a week -- so narrowing to it opens the range, and leaving it
  // puts the default back rather than leaving the whole log open.
  const columns = useMemo(() => buildColumns((next) => {
    void navigate({
      search: (prev) => ({ ...prev, about: `${next.type}:${next.id}`, aboutName: next.label, from: "all" }),
      ...searchUpdate,
    });
  }), [navigate]);
  function clearEntity() {
    setParams({ about: undefined, aboutName: undefined, from: params.from === "all" ? undefined : params.from });
  }

  // Instants for the query, from the days the inputs hold. Start of the first
  // day, end of the last, both in local time -- the day the reader means. A
  // cleared "from" falls back to the beginning rather than to an invalid date.
  const fromMs = fromDay === "" ? 0 : new Date(`${fromDay}T00:00:00`).getTime();
  const toMs = toDay === "" ? null : new Date(`${toDay}T23:59:59.999`).getTime();

  const result = useQuery(api.audit.list, {
    fromMs,
    toMs,
    personId: (personIds[0] ?? null) as Id<"persons"> | null,
    action: action === "" ? null : action,
    entity: entity === null ? null : { type: entity.type, id: entity.id },
  });
  const people = useQuery(api.audit.people, {});
  const personOptions = useMemo(
    () => (people ?? []).map((person) => ({ value: String(person.personId), label: person.name })),
    [people],
  );

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Audit log"
        description="Who did what in CeMMbase, newest first."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Administration", to: "/admin" }, { label: "Audit log" }]}
      />

      <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <Stack as="section" gap={4}>
            <Stack direction={{ base: "column", lg: "row" }} gap={3} align={{ lg: "flex-start" }}>
              <Field.Root w={{ base: "full", lg: "11rem" }}>
                <Field.Label fontSize="sm">From</Field.Label>
                <Input
                  size="sm"
                  type="date"
                  value={fromDay}
                  max={toDay || undefined}
                  // Emptied means from the beginning; the week is the default.
                  onChange={(event) => setParams({ from: event.target.value === "" ? "all" : event.target.value })}
                />
              </Field.Root>
              <Field.Root w={{ base: "full", lg: "11rem" }}>
                <Field.Label fontSize="sm">To</Field.Label>
                <Input
                  size="sm"
                  type="date"
                  value={toDay}
                  min={fromDay || undefined}
                  onChange={(event) => setParams({ to: event.target.value === "" ? undefined : event.target.value })}
                />
              </Field.Root>
              <Box flex="1" minW={0}>
                <MultiSelectTags
                  label="Person — did it, or it was about them"
                  placeholder={people === undefined ? "Loading people…" : "Anybody"}
                  emptyMessage="Nobody matches that."
                  options={personOptions}
                  selected={personIds}
                  // One person at a time: the newest pick replaces the last.
                  onChange={(next) => setParams({ person: next[next.length - 1] })}
                />
              </Box>
              <Field.Root w={{ base: "full", lg: "18rem" }}>
                <Field.Label fontSize="sm">Act</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={action}
                    onChange={(event) => setParams({ act: event.target.value === "" ? undefined : event.target.value })}
                  >
                    <option value="">Every act</option>
                    {ACT_GROUPS.map((group) => (
                      <optgroup key={group.module} label={group.label}>
                        <option value={`${group.module}.`}>Every {group.label.toLowerCase()} act</option>
                        {group.actions.map((value) => (
                          <option key={value} value={value}>{actionLabel(value).verb}</option>
                        ))}
                      </optgroup>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            </Stack>
            {entity === null ? null : (
              <HStack>
                <Tag.Root size="lg" colorPalette="blue">
                  <Tag.Label>Only entries about {entity.label}</Tag.Label>
                  <Tag.EndElement>
                    <Tag.CloseTrigger aria-label="Show entries about everything" onClick={clearEntity} />
                  </Tag.EndElement>
                </Tag.Root>
              </HStack>
            )}
        </Stack>

        {result === undefined ? (
          <TableSkeleton rows={9} columns={5} />
        ) : (
          <Stack gap={2}>
            {/* Filtered on the server, so there is no total to give. Clearing
                goes back to the last week rather than the whole log: the
                week is where the page opens, and all of it is a lot to load. */}
            <FilterCount
              shown={result.entries.length}
              total={null}
              noun={["entry", "entries"]}
              filtered={Object.values(params).some((value) => value !== undefined)}
              onClear={() => void navigate({ search: {}, ...searchUpdate })}
            >
              {result.truncated ? ", the newest only: there are more in this range. Narrow the dates to see the rest." : null}
            </FilterCount>
            <Box as="section" borderWidth="1px" borderColor="border" shadow="sm" bg="bg">
              <DataTable
                data={result.entries}
                columns={columns}
                getRowId={(entry) => entry._id}
                emptyMessage="Nothing recorded for these filters."
                stickyHeader
                size="sm"
                interactive={false}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
